import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';

/** Client-only: drives individual checkbox visuals without extra `/cart/update.js` churn. */
const INDIVIDUAL_WRAP_KEYS_STORAGE = 'fraegra_gift_wrap_individual_line_keys';

const GIFT_WRAP_TITLES_ATTR = '_gift_wrap_titles';
const LEGACY_GIFT_WRAP_SELECTION_ATTR = '_gift_wrap_selection';

function readIndividualWrapKeys() {
  try {
    const raw = sessionStorage.getItem(INDIVIDUAL_WRAP_KEYS_STORAGE);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((k) => typeof k === 'string' && k) : []);
  } catch {
    return new Set();
  }
}

function writeIndividualWrapKeys(set) {
  sessionStorage.setItem(INDIVIDUAL_WRAP_KEYS_STORAGE, JSON.stringify([...set]));
}

function addIndividualWrapKey(key) {
  if (!key) return;
  const s = readIndividualWrapKeys();
  s.add(key);
  writeIndividualWrapKeys(s);
}

function removeIndividualWrapKey(key) {
  if (!key) return;
  const s = readIndividualWrapKeys();
  s.delete(key);
  writeIndividualWrapKeys(s);
}

function clearIndividualWrapKeys() {
  sessionStorage.removeItem(INDIVIDUAL_WRAP_KEYS_STORAGE);
}

function getWrapVariantId() {
  const el =
    document.querySelector('gift-wrap-content[data-gift-wrap-variant-id]') ||
    document.querySelector('gift-wrap-product-content[data-gift-wrap-variant-id]');
  const id = parseInt(el?.dataset.giftWrapVariantId || '', 10);
  return Number.isFinite(id) ? id : null;
}

async function fetchCartJson() {
  const res = await fetch('/cart.js');
  if (!res.ok) throw new Error('cart');
  return res.json();
}

function getWrapLineItem(cart, wrapVariantId) {
  return (
    cart.items?.find((i) => {
      if (Number(i.variant_id) !== wrapVariantId) return false;
      const p = i.properties;
      if (!p) return true;
      return p._gift_wrapping_product === true || p._gift_wrapping_product === 'true';
    }) ?? null
  );
}

function findWrapLine(cart, wrapVariantId) {
  return getWrapLineItem(cart, wrapVariantId) != null;
}

function isWrapFeeLineItem(item, wrapVariantId) {
  if (!item || Number(item.variant_id) !== wrapVariantId) return false;
  const p = item.properties;
  if (!p) return true;
  return p._gift_wrapping_product === true || p._gift_wrapping_product === 'true';
}

function lineProductTitle(item) {
  const t = item?.product_title || item?.title;
  return typeof t === 'string' ? t.trim() : '';
}

function collectTitlesFromDomGiftWrapRows() {
  return [
    ...new Set(
      [...document.querySelectorAll('[data-gift-wrap-line-title]')]
        .map((el) => (el.getAttribute('data-gift-wrap-line-title') || '').trim())
        .filter(Boolean)
    ),
  ];
}

function collectIndividualGiftWrapTitles(cart, wrapVariantId, lineKeys) {
  const keys = lineKeys instanceof Set ? lineKeys : new Set(lineKeys || []);
  const titles = [];
  for (const item of cart.items || []) {
    if (!keys.has(item.key)) continue;
    if (isWrapFeeLineItem(item, wrapVariantId)) continue;
    const title = lineProductTitle(item);
    if (title) titles.push(title);
  }
  return [...new Set(titles)];
}

function collectFallbackMerchandiseTitles(cart, wrapVariantId) {
  const titles = [];
  for (const item of cart.items || []) {
    if (isWrapFeeLineItem(item, wrapVariantId)) continue;
    const title = lineProductTitle(item);
    if (title) titles.push(title);
  }
  return [...new Set(titles)];
}

function titlesFromAttributeRaw(raw) {
  if (raw == null || raw === '') return [];
  try {
    const a = JSON.parse(raw);
    return Array.isArray(a) ? a.map((x) => String(x)) : [];
  } catch {
    return [];
  }
}

function titlesEqual(a, b) {
  if (a.length !== b.length) return false;
  const sa = [...a].map(String).sort();
  const sb = [...b].map(String).sort();
  return sa.every((v, i) => v === sb[i]);
}

/**
 * Keeps cart attribute in sync with wrap state. Does not dispatch cartUpdated (avoids drawer flash).
 * @param {Set<string>} lineKeys
 */
async function syncGiftWrapTitlesAttribute(cart, wrapVariantId, wrapItem, footerOn, lineKeys) {
  let titles = [];
  if (!wrapItem) {
    titles = [];
  } else if (footerOn) {
    titles = collectTitlesFromDomGiftWrapRows();
    if (titles.length === 0) titles = collectFallbackMerchandiseTitles(cart, wrapVariantId);
  } else {
    titles = collectIndividualGiftWrapTitles(cart, wrapVariantId, lineKeys);
  }

  const prevTitles = titlesFromAttributeRaw(cart.attributes?.[GIFT_WRAP_TITLES_ATTR]);
  const hadLegacy = Boolean(cart.attributes?.[LEGACY_GIFT_WRAP_SELECTION_ATTR]);
  if (titlesEqual(prevTitles, titles) && !hadLegacy) return;

  const attrs = { ...(cart.attributes || {}) };
  delete attrs[LEGACY_GIFT_WRAP_SELECTION_ATTR];
  if (titles.length === 0) delete attrs[GIFT_WRAP_TITLES_ATTR];
  else attrs[GIFT_WRAP_TITLES_ATTR] = JSON.stringify(titles);

  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: attrs }),
  });
  if (!res.ok) throw new Error('cart update');
}

async function refreshGiftWrapTitlesAttribute() {
  const vid = getWrapVariantId();
  if (!vid) return;
  try {
    const cart = await fetchCartJson();
    const wrapItem = getWrapLineItem(cart, vid);
    const footerOn = wrapItem ? isEntireOrderWrapLineItem(wrapItem) : false;
    const cartLineKeys = new Set((cart.items || []).map((i) => i.key).filter(Boolean));
    const stored = readIndividualWrapKeys();
    let pruned = false;
    for (const k of [...stored]) {
      if (!cartLineKeys.has(k)) {
        stored.delete(k);
        pruned = true;
      }
    }
    if (pruned) writeIndividualWrapKeys(stored);
    await syncGiftWrapTitlesAttribute(cart, vid, wrapItem, footerOn, stored);
  } catch (err) {
    console.warn('[gift-wrap] titles attribute', err);
  }
}

function isEntireOrderWrapLineItem(item) {
  if (!item?.properties) return false;
  const v = item.properties._gift_wrap_entire_order;
  return v === true || v === 'true';
}

function dispatchCartUpdated() {
  ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
  ThemeBus.dispatchEvent(new Event(EVENTS.cartItemAdded));
}

async function upgradeWrapLineToEntireOrder(cart, item) {
  const line = (cart.items?.indexOf(item) ?? -1) + 1;
  if (line < 1) throw new Error('wrap line');
  const props = { ...(item.properties || {}) };
  props._gift_wrapping_product = props._gift_wrapping_product ?? 'true';
  props._gift_wrap_entire_order = 'true';
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      line,
      quantity: item.quantity,
      properties: props,
    }),
  });
  if (!res.ok) throw new Error('change');
}

async function ensureWrapInCart(wrapVariantId, options = {}) {
  const entireOrder = !!options.entireOrder;
  const cart = await fetchCartJson();
  const existing = getWrapLineItem(cart, wrapVariantId);

  if (entireOrder) {
    clearIndividualWrapKeys();
    if (existing && isEntireOrderWrapLineItem(existing)) return;
    if (existing && !isEntireOrderWrapLineItem(existing)) {
      await upgradeWrapLineToEntireOrder(cart, existing);
      dispatchCartUpdated();
      document.querySelector('cart-drawer-items')?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const fd = new FormData();
    fd.append('id', wrapVariantId);
    fd.append('quantity', 1);
    fd.append('properties[_gift_wrapping_product]', 'true');
    fd.append('properties[_gift_wrap_entire_order]', 'true');
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: fd,
    });
    const data = await res.json();
    if (data.status && data.status !== 200) throw new Error(data.message || 'add');
    document.querySelector('cart-drawer-items')?.scrollTo({ top: 0, behavior: 'smooth' });
    dispatchCartUpdated();
    return;
  }

  if (existing) return;
  const fd = new FormData();
  fd.append('id', wrapVariantId);
  fd.append('quantity', 1);
  fd.append('properties[_gift_wrapping_product]', 'true');
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: fd,
  });
  const data = await res.json();
  if (data.status && data.status !== 200) throw new Error(data.message || 'add');
  document.querySelector('cart-drawer-items')?.scrollTo({ top: 0, behavior: 'smooth' });
  dispatchCartUpdated();
}

async function removeWrapFromCart(wrapVariantId) {
  const cart = await fetchCartJson();
  const item = getWrapLineItem(cart, wrapVariantId);
  if (!item) return;
  const idx = cart.items.indexOf(item);
  if (idx === -1) return;
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ line: idx + 1, quantity: 0 }),
  });
  if (!res.ok) throw new Error('change');
  ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
}

async function syncGiftWrapCheckboxes() {
  const vid = getWrapVariantId();
  if (!vid) return;
  try {
    const cart = await fetchCartJson();
    const wrapItem = getWrapLineItem(cart, vid);
    const footerOn = wrapItem ? isEntireOrderWrapLineItem(wrapItem) : false;
    const footer = document.querySelector('[data-gift-wrap-checkbox]');
    if (footer instanceof HTMLInputElement) footer.checked = footerOn;

    const cartLineKeys = new Set((cart.items || []).map((i) => i.key).filter(Boolean));
    const stored = readIndividualWrapKeys();
    let pruned = false;
    for (const k of [...stored]) {
      if (!cartLineKeys.has(k)) {
        stored.delete(k);
        pruned = true;
      }
    }
    if (pruned) writeIndividualWrapKeys(stored);

    if (!wrapItem) {
      clearIndividualWrapKeys();
      document.querySelectorAll('[data-individual-gift-wrap-checkbox]').forEach((cb) => {
        if (cb instanceof HTMLInputElement) cb.checked = false;
      });
      await syncGiftWrapTitlesAttribute(cart, vid, null, false, new Set());
      return;
    }

    await syncGiftWrapTitlesAttribute(cart, vid, wrapItem, footerOn, stored);

    if (footerOn) {
      clearIndividualWrapKeys();
      document.querySelectorAll('[data-individual-gift-wrap-checkbox]').forEach((cb) => {
        if (cb instanceof HTMLInputElement) cb.checked = false;
      });
      return;
    }

    document.querySelectorAll('[data-individual-gift-wrap-checkbox]').forEach((cb) => {
      if (!(cb instanceof HTMLInputElement)) return;
      const lineKey = cb.dataset.cartLineKey || '';
      cb.checked = Boolean(lineKey && stored.has(lineKey));
    });
  } catch (err) {
    console.warn('[gift-wrap] sync', err);
  }
}

function scheduleSync() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      void syncGiftWrapCheckboxes();
      window.setTimeout(() => void syncGiftWrapCheckboxes(), 200);
    });
  });
}

function onDrawerChange(e) {
  const t = e.target;
  if (!(t instanceof HTMLInputElement)) return;
  const vid = getWrapVariantId();
  if (!vid) return;

  if (t.matches('[data-gift-wrap-checkbox]')) {
    (async () => {
      try {
        if (t.checked) await ensureWrapInCart(vid, { entireOrder: true });
        else await removeWrapFromCart(vid);
      } catch (err) {
        console.error('[gift-wrap]', err);
        t.checked = !t.checked;
      }
    })();
    return;
  }

  if (t.matches('[data-individual-gift-wrap-checkbox]')) {
    const lineKey = t.dataset.cartLineKey || '';

    if (!t.checked) {
      (async () => {
        try {
          removeIndividualWrapKey(lineKey);
          const cart = await fetchCartJson();
          const wrapItem = getWrapLineItem(cart, vid);
          const keys = readIndividualWrapKeys();
          if (keys.size === 0 && wrapItem && !isEntireOrderWrapLineItem(wrapItem)) {
            await removeWrapFromCart(vid);
          } else {
            await refreshGiftWrapTitlesAttribute();
            ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
          }
        } catch (err) {
          console.error('[gift-wrap]', err);
          t.checked = true;
        }
      })();
      return;
    }

    (async () => {
      try {
        addIndividualWrapKey(lineKey);
        const cart = await fetchCartJson();
        if (!findWrapLine(cart, vid)) await ensureWrapInCart(vid, { entireOrder: false });
        await refreshGiftWrapTitlesAttribute();
        scheduleSync();
      } catch (err) {
        console.error('[gift-wrap]', err);
        removeIndividualWrapKey(lineKey);
        t.checked = false;
      }
    })();
  }
}

const drawer = document.querySelector('cart-drawer-component');
if (drawer) drawer.addEventListener('change', onDrawerChange);

ThemeBus.addEventListener(EVENTS.cartUpdated, scheduleSync);
ThemeBus.addEventListener(EVENTS.cartDrawerOpen, scheduleSync);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleSync);
} else {
  scheduleSync();
}
